import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { formatCheckedAgo, SOURCE_LABELS } from '../modelcatalog/review/reviewUtils';
import type { Model, ModelCatalogReview, ModelConfig, ModelProvider } from '../../types';
import { differenceCounts, hasPlatformKey, lastChecked, plural } from './modelsPageData';

/**
 * The Models page's summary strip (task 1167): providers (and how many lack a
 * key), Models by status, configs, when the catalog was last checked, and the
 * review's open differences linking to the Review view. The differences tile
 * is omitted when no review document has loaded.
 */

interface ModelsSummaryStripProps {
  providers: ModelProvider[];
  models: Model[];
  configs: ModelConfig[];
  review: ModelCatalogReview | undefined;
  reviewPath: string | null;
}

function Tile({ label, value, sub, testId }: { label: string; value: ReactNode; sub?: ReactNode; testId: string }) {
  return (
    <div data-testid={testId} className="min-w-[9rem] flex-1 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</p>}
    </div>
  );
}

export function ModelsSummaryStrip({ providers, models, configs, review, reviewPath }: ModelsSummaryStripProps) {
  const enabled = providers.filter((p) => p.enabled).length;
  const withoutKey = providers.filter((p) => !hasPlatformKey(p)).length;
  const byStatus = {
    active: models.filter((m) => m.status === 'active').length,
    deprecated: models.filter((m) => m.status === 'deprecated').length,
    retired: models.filter((m) => m.status === 'retired').length,
  };
  const modelsWithConfigs = new Set(configs.map((c) => c.modelId)).size;
  const checked = lastChecked(models);
  const differences = differenceCounts(review);
  const sources = (review?.sources ?? []).map((s) => SOURCE_LABELS[s.id] ?? s.id).join(', ');

  const statusParts = [
    `${byStatus.active} active`,
    byStatus.deprecated ? `${byStatus.deprecated} deprecated` : null,
    byStatus.retired ? `${byStatus.retired} retired` : null,
  ].filter(Boolean);

  return (
    <div data-testid="models-summary-strip" className="mt-6 flex flex-wrap gap-3">
      <Tile
        testId="models-summary-providers"
        label="Providers"
        value={providers.length}
        sub={`${enabled} enabled · ${withoutKey} without a key`}
      />
      <Tile testId="models-summary-models" label="Models" value={models.length} sub={statusParts.join(' · ')} />
      <Tile
        testId="models-summary-configs"
        label="Model Configs"
        value={configs.length}
        sub={`on ${plural(modelsWithConfigs, 'Model', 'Models')}`}
      />
      <Tile
        testId="models-summary-checked"
        label="Last checked"
        value={checked ? formatCheckedAgo(checked) : 'never'}
        sub={sources || undefined}
      />
      {review && reviewPath && (
        <Tile
          testId="models-summary-differences"
          label="Differences"
          value={differences.total}
          sub={
            <Link to={reviewPath} className="text-indigo-600 hover:underline dark:text-indigo-400">
              Open Review
              {differences.total > 0
                ? ` · ${[
                    differences.prices ? plural(differences.prices, 'price', 'prices') : null,
                    differences.limits ? plural(differences.limits, 'limit', 'limits') : null,
                  ]
                    .filter(Boolean)
                    .join(', ')}`
                : ''}
            </Link>
          }
        />
      )}
    </div>
  );
}

export default ModelsSummaryStrip;
